// @workflow platform.viewer
/**
 * Read-only questions about the 3D viewer: is it there, is its model, tree and main extension
 * loaded, is its canvas on screen. Nothing here loads or changes anything.
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */

/** The app's main Forge extension (extensions/mainExtension.js registers it under this name). */
export const MAIN_EXTENSION = 'MainExtension';

/**
 * The main extension if it is already loaded, else null. `viewer.getExtension` only reads;
 * `forgeService.getMainExtension()` would load the extension as a side effect (forgeService.js:2325).
 */
export function loadedMainExtension(viewer) {
  return viewer && typeof viewer.getExtension === 'function' ? viewer.getExtension(MAIN_EXTENSION) : null;
}

/** The model's instance tree, or null while the model or its tree is not there yet. */
export function instanceTreeOf(viewer) {
  const model = viewer && viewer.model;
  return model && typeof model.getInstanceTree === 'function' ? model.getInstanceTree() : null;
}

/**
 * The DOM half of `isViewer3DUsableInPlace` (utils/viewerNavigationUtils.js): the 3D canvas is
 * attached and not in a hidden pane. Its other half waits for ALL geometry, which a large model
 * can take minutes to stream; callers here only need the model, its tree and the main extension.
 */
export function isCanvasOnScreen(viewer) {
  const el = viewer && viewer.container;
  return !!el && el.isConnected && el.offsetParent !== null;
}

/**
 * @param {object|null} viewer the Forge viewer (store.state.viewer)
 * @returns {{ hasViewer: boolean, hasModel: boolean, hasInstanceTree: boolean, hasMainExtension: boolean, isOnScreen: boolean }}
 */
export function readinessOf(viewer) {
  return {
    hasViewer: !!viewer,
    hasModel: !!(viewer && viewer.model),
    hasInstanceTree: !!instanceTreeOf(viewer),
    hasMainExtension: !!loadedMainExtension(viewer),
    isOnScreen: isCanvasOnScreen(viewer),
  };
}

/** Every readiness flag is true: the model can be isolated, coloured and framed now. */
export function isReadyState(state) {
  return !!(state && state.hasViewer && state.hasModel && state.hasInstanceTree && state.hasMainExtension && state.isOnScreen);
}
